import { getApiConfig } from "@/config/api.config";
import { AuthResponse, LoginCredentials } from "@/types/auth";
import { getCurrentRegion, Region } from "@/utils/environment";
import { UserRole } from "@/utils/roleManager";

const TOKEN_KEY = "auth_token";
const USER_KEY = "auth_user";
const SSO_KEY_PREFIX = "sso_login_";

export interface SsoProvider {
  id: number;
  name: string;
}

export class LoginError extends Error {
  public readonly statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = "LoginError";
    this.statusCode = statusCode;
  }
}

// The API answers SSO configuration mistakes (for example a callback outside its allowlist) with the reason in
// metadata.errors; everything else is deliberately generic.
async function ssoErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { metadata?: { errors?: unknown } };
    const first = Array.isArray(body.metadata?.errors) ? body.metadata.errors[0] : undefined;
    if (typeof first === "string" && first.trim()) {
      return first;
    }
  } catch {
    // Not JSON; fall through to the generic message.
  }
  return "SSO login failed";
}

export class CoreService {
  private static instance: CoreService;
  private authToken: string | null = null;

  private constructor() {
    // Initialize from localStorage if exists
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      try {
        // Verify token format/validity if needed
        this.authToken = storedToken;
      } catch {
        this.clearAuth();
      }
    }
  }

  public static getInstance(): CoreService {
    if (!CoreService.instance) {
      CoreService.instance = new CoreService();
    }
    return CoreService.instance;
  }

  private clearAuth(): void {
    this.authToken = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // For development testing only
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK === "true") {
        const mockResponse: AuthResponse = {
          authorizationToken: "mock-token-123",
          user: {
            id: 1,
            email: credentials.username,
            firstName: "Mock",
            lastName: "User",
            role: UserRole.CUSTOMER_ADMIN,
            customerId: 123,
            allowedStores: [],
            token: "mock-token-123",
          },
        };

        this.setAuth(mockResponse);
        return mockResponse;
      }

      const config = getApiConfig();
      const currentRegion = getCurrentRegion();

      // Real API call with direct URL
      const response = await fetch(`${config.core}/auth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Crave-Region": currentRegion.toString(),
        },
        body: JSON.stringify({
          username: credentials.username.trim(),
          password: credentials.password.trim(),
        }),
      });

      if (!response.ok) {
        throw new LoginError("Login failed", response.status);
      }

      const responseText = await response.text();

      const responseData = JSON.parse(responseText);

      // Extract token and user data from the response
      const { authorizationToken, user } = responseData.data || responseData;

      if (!authorizationToken) {
        throw new LoginError("No authorization token received");
      }

      if (!user) {
        throw new LoginError("No user data received");
      }

      const authResponse: AuthResponse = {
        authorizationToken,
        user: {
          id: parseInt(user.id) || 0,
          email: user.email || "",
          firstName: user.firstName || user.first_name || "",
          lastName: user.lastName || user.last_name || "",
          role: user.role || UserRole.NONE,
          customerId: parseInt(user.customerId || user.company_id) || undefined,
          customerName: user.customerName,
          allowedStores: user.allowedStores || [],
          token: authorizationToken,
        },
      };

      // Validate that the role is a known role type
      if (!Object.values(UserRole).includes(authResponse.user.role)) {
        authResponse.user.role = UserRole.NONE;
      }

      this.setAuth(authResponse);
      return authResponse;
    } catch (error) {
      if (error instanceof LoginError) {
        throw error;
      }

      if (error instanceof TypeError && error.message === "Failed to fetch") {
        throw new LoginError("Failed to fetch");
      }

      if (error instanceof Error) {
        throw new LoginError(error.message);
      }

      throw new LoginError("Login failed");
    }
  }

  private setAuth(authResponse: AuthResponse): void {
    this.authToken = authResponse.authorizationToken;
    localStorage.setItem(TOKEN_KEY, authResponse.authorizationToken);
    localStorage.setItem(USER_KEY, JSON.stringify(authResponse.user));
  }

  private async ssoRequest<T>(endpoint: string, body: object): Promise<T> {
    const response = await fetch(`${getApiConfig().core}/auth/sso/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Crave-Region": getCurrentRegion().toString(),
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new LoginError(await ssoErrorMessage(response), response.status);
    }
    const result = (await response.json()) as { data: T };
    return result.data;
  }

  /** `login` is the user's email or username; the API resolves either to one account. */
  public discoverSsoProviders(login: string): Promise<SsoProvider[]> {
    return this.ssoRequest("providers", { login: login.trim(), platform: "WEB" });
  }

  public async startSso(login: string, providerId: number): Promise<string> {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const base64url = (value: Uint8Array) =>
      btoa(String.fromCharCode(...value))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
    const codeVerifier = base64url(bytes);
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
    const result = await this.ssoRequest<{ authorizationUrl: string; state: string }>("start", {
      login: login.trim(),
      providerId,
      codeChallenge: base64url(new Uint8Array(digest)),
      platform: "WEB",
      // The API only accepts callbacks on its allowlist; naming ours lets one API serve several hosts.
      redirectUri: `${window.location.origin}/login/sso/callback`,
    });
    if (
      !/^[A-Za-z0-9_-]{43}$/.test(result.state) ||
      new URL(result.authorizationUrl).protocol !== "https:"
    ) {
      throw new LoginError("Invalid SSO response");
    }
    sessionStorage.setItem(
      `${SSO_KEY_PREFIX}${result.state}`,
      JSON.stringify({ codeVerifier, createdAt: Date.now(), region: getCurrentRegion() }),
    );
    return result.authorizationUrl;
  }

  public async completeSso(state: string, code: string): Promise<AuthResponse> {
    if (!/^[A-Za-z0-9_-]{43}$/.test(state) || !code) {
      throw new LoginError("Invalid SSO callback");
    }
    const key = `${SSO_KEY_PREFIX}${state}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      throw new LoginError("SSO login must start in this browser tab");
    }
    sessionStorage.removeItem(key);
    const attempt = JSON.parse(stored) as {
      codeVerifier: string;
      createdAt: number;
      region: Region;
    };
    if (Date.now() - attempt.createdAt > 600_000 || attempt.region !== getCurrentRegion()) {
      throw new LoginError("SSO login expired or region changed");
    }
    const result = await this.ssoRequest<AuthResponse>("exchange", {
      state,
      code,
      codeVerifier: attempt.codeVerifier,
    });
    if (
      !result.authorizationToken ||
      !result.user?.id ||
      !Object.values(UserRole).includes(result.user.role)
    ) {
      throw new LoginError("Invalid SSO login response");
    }
    const authResponse: AuthResponse = {
      authorizationToken: result.authorizationToken,
      user: {
        id: result.user.id,
        email: result.user.email || "",
        firstName: result.user.firstName || "",
        lastName: result.user.lastName || "",
        role: result.user.role,
        customerId: result.user.customerId,
        customerName: result.user.customerName,
        token: result.authorizationToken,
        allowedStores: result.user.allowedStores || [],
      },
    };
    this.setAuth(authResponse);
    return authResponse;
  }

  public logout(): void {
    this.clearAuth();
  }

  public getAuthorizationHeader(): string | null {
    return this.authToken;
  }

  public isAuthenticated(): boolean {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      this.clearAuth();
      return false;
    }
    if (storedToken !== this.authToken) {
      this.authToken = storedToken;
    }
    return true;
  }

  public getStoredUser() {
    try {
      const userData = localStorage.getItem(USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      this.clearAuth();
      return null;
    }
  }
}
